"use client";

import styled, { type CSSProperties } from "styled-components";
import React, { useEffect, useState } from "react";

export interface InputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputTitle?: string;
  readOnly?: boolean;
  disabled?: boolean;
  color: string;
  backgroundColor: string;
  styles?: CSSProperties;
  type?: string;
  maxLength?: number;
  className?: string;
  placeholder?: string;
  required?: boolean;
  min?: string;
  max?: string;
  bgColor?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: boolean;
}

interface IInputStyle {
  $disabled?: boolean;
  $color: string;
  $backgroundColor: string;
  $maxWidth?: string;
  $error?: boolean;
}

const StyledInputWrapper = styled.div<IInputStyle>`
  display: flex;
  flex-direction: column;
  height: fit-content;
  max-height: 40px;
  max-width: ${(props) => (props.$maxWidth ? props.$maxWidth : "300px")};
  width: 100%;
  min-width: 300px;
  position: relative;
  flex: 1;

  .title {
    position: absolute;
    top: -12px;
    left: 12px;
    z-index: 1;
    padding: 0px 4px;
    font-size: 0.75rem;
    text-align: left;
    font-weight: 400;
    background-color: ${(props) => props.theme.white};

    opacity: ${(props) => (props.$disabled ? "0.2" : "1")};
    color: ${(props) => (props.$error ? props.theme.main : "inherit")};
    span {
      color: ${(props) => props.theme.main};
    }
  }

  @media (max-width: 1024px) {
    .title {
      font-size: 0.75rem;
    }
  }
`;

export const StyledInputField = styled.input<IInputStyle>`
  background-color: ${(props) =>
    props.$backgroundColor ? props.$backgroundColor : props.theme.white};

  border: 1px solid
    ${(props) =>
      props.$disabled ? `${props.theme.lightGrey}` : `${props.theme.main}`};
  border-radius: 5px;
  position: relative;
  display: flex;
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  color: ${(props) =>
    props.$disabled ? "#d5d5d5" : props.$error ? props.theme.main : "#141414"};
  text-overflow: ellipsis;
  overflow: hidden;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    background-color: #f9f9f9;
  }

  &::placeholder {
    opacity: 0.4;
  }

  @media (max-width: 1024px) {
    font-size: 0.85rem;
  }
`;

export function InputField({
  value,
  onChange,
  inputTitle,
  readOnly,
  disabled = false,
  color,
  backgroundColor,
  styles,
  type,
  maxLength,
  className,
  placeholder,
  required,
  min,
  max,
  onBlur,
  error,
}: InputProps) {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <StyledInputWrapper
      className={className}
      $disabled={disabled}
      $color={color}
      $backgroundColor={backgroundColor}
      $error={error}
      style={styles}
    >
      {inputTitle && (
        <div className="title">
          {inputTitle}
          {required && <span>*</span>}
        </div>
      )}
      <StyledInputField
        type={type || "text"}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e);
        }}
        readOnly={readOnly}
        disabled={disabled}
        $disabled={disabled}
        $color={color}
        $backgroundColor={backgroundColor}
        $error={error}
        min={type === "number" ? (min ? min : "0") : undefined}
        max={max ? max : undefined}
        maxLength={maxLength}
        onBlur={onBlur}
        onInput={(e) => {
          if (type === "tel") {
            const input = e as unknown as React.ChangeEvent<HTMLInputElement>;
            return onChange(input);
          }
          return undefined;
        }}
        placeholder={placeholder}
        onKeyDown={(e) => {
          // allow: backspace, delete, tab, arrows
          if (className === "reminder-due" || className === "reminder-count") {
            if (
              [
                "Backspace",
                "Delete",
                "Tab",
                "ArrowLeft",
                "ArrowRight",
              ].includes(e.key)
            ) {
              return;
            }
            // block non-digits
            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
            }

            if (className === "reminder-count") {
              if (e.key !== "1" && e.key !== "2") {
                e.preventDefault();
              }
              // If there's already a value, prevent additional input
              if (
                e.currentTarget.value &&
                !["Backspace", "Delete"].includes(e.key)
              ) {
                e.preventDefault();
              }
              return;
            }
          }
        }}
      />
    </StyledInputWrapper>
  );
}
